import { Card, Tag, Typography, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import type { Customer } from '~app/graph/generated/graphql'

/*
 * Component
 */

interface Props {
  customer: Pick<
    Customer,
    | 'address'
    | 'companyName'
    | 'email'
    | 'encodedPublicId'
    | 'firstName'
    | 'lastName'
  >
}

/**
 * Who the customer is, as a header rather than a form. Nothing here is
 * editable, so it reads as one strip instead of six table rows that push the
 * settings below the fold.
 */
const IdentitySection = ({ customer }: Props) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const address = customer.address
    ? `${customer.address.lines.join(' ')}, ${
        customer.address.streetAddress
      }, ${customer.address.city} ${customer.address.zip}, ${
        customer.address.countryCode
      }`
    : t('misc.noAddress')

  const details = [
    { label: t('misc.address'), value: address },
    { label: t('misc.companyName'), value: customer.companyName || '—' },
    { label: t('misc.email'), value: customer.email },
  ]

  return (
    <Card size="small">
      <div
        style={{
          alignItems: 'baseline',
          display: 'flex',
          flexWrap: 'wrap',
          gap: token.marginXS,
          marginBottom: token.marginXS,
        }}
      >
        <Typography.Text strong>
          {[customer.firstName, customer.lastName].filter(Boolean).join(' ')}
        </Typography.Text>
        <Tag style={{ marginInlineEnd: 0 }}>{customer.encodedPublicId}</Tag>
      </div>
      <div
        style={{
          display: 'grid',
          gap: `${token.marginXS}px ${token.marginLG}px`,
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        }}
      >
        {details.map(detail => (
          <div key={detail.label} style={{ minWidth: 0 }}>
            <div
              style={{
                color: token.colorTextDescription,
                fontSize: token.fontSizeSM,
              }}
            >
              {detail.label}
            </div>
            <div style={{ overflowWrap: 'anywhere' }}>{detail.value}</div>
          </div>
        ))}
      </div>
    </Card>
  )
}

export default IdentitySection
